import { InputBase, styled } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useRef, useState } from "react";
import { CachePolicies } from "use-http";

import { JANZU_URL } from "config/config";
import { useAppDispatch } from "hooks/store";
import { useFetchCustom } from "hooks/useFetchCustom";
import { HubWithStatus } from "models/hub";
import { Modal } from "new-components/modals/Modal";
import { setHubName } from "store/slices/Hubs/hubsSlice";


const CustomTextField = styled(InputBase)(() => ({
  "& .MuiInputBase-input": {
    border: "1px solid #E5E9ED",
    backgroundColor: "white",
    fontFamily: "inherit",
    fontSize: "20px",
    fontWeight: 400,
    lineHeight: "24px",
    padding: "8px 12px",
    letterSpacing: "0.5px",
    borderRadius: "4px",
  },
  "&.Mui-error": {
    border: "1px solid #d32f2f",
  },
  "&.Mui-focused": {
    border: "none",
  },
}));

interface HubChangeNameDialogProps {
  isDialogOpen: boolean;
  onCloseDialog: () => void;
  hubId: string;
  hubCurrentName: string;
}

export function HubChangeNameDialog(props: HubChangeNameDialogProps) {
  const { isDialogOpen, onCloseDialog, hubId, hubCurrentName } = props;

  const [hubNewName, setHubNewName] = useState<string>(hubCurrentName);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { patch: patchHub, loading: isLoading } = useFetchCustom<HubWithStatus>(`${JANZU_URL}reef-api/hubs/${hubId}`, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  const hasHubNameChanged = hubNewName === hubCurrentName;
  const cantSubmit = hubNewName.trim().length === 0 || isLoading || hasHubNameChanged;

  const { enqueueSnackbar } = useSnackbar();
  const handleNewNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHubNewName((event.target as HTMLInputElement).value);
  };

  const dispatch = useAppDispatch();

  const handleSubmit = async () => {
    try {
      await patchHub({ name: hubNewName });
      dispatch(setHubName({ id: hubId, name: hubNewName }));
      enqueueSnackbar("Vision Core name changed successfully", { variant: "success" });
      onCloseDialog();
    } catch (error) {
      throw new Error(`Invalid response from API ${error}`);
    }
  };

  const handleInputClick = () => {
    if (hasHubNameChanged) inputRef.current?.select();
  };

  return (
    <Modal
      open={isDialogOpen}
      onClose={onCloseDialog}
      size="small"
      title="Change Vision Core name"
      onCancel={onCloseDialog}
      onConfirmation={handleSubmit}
      confirmationButtonTitle="Save"
      isConfirmationDisabled={cantSubmit}
      body={
        <CustomTextField
          value={hubNewName}
          onChange={handleNewNameChange}
          inputRef={inputRef}
          onClick={handleInputClick}
          required
          placeholder="Type a new Vision Core name"
          fullWidth
        />
      }
    />
  );
}
